import { useMemo, useRef, useState } from 'react';
import { Accordion, useClickOutsideHandler } from '@web/utils';
import { getCommonLocationIds } from '../../getCommonLocationIds';
import { DeliveryType, Location, OrderDelivery } from '../types';

interface MoveToToggleMenuProps {
  delivery: OrderDelivery;
  selectedCart: OrderDelivery[] | [];
  selectedLocations: Location[][];
  t: any;
  setIsOpened: (isOpened: boolean) => void;
  handleMoveItem: (toid: string | null) => void;
  handleDeleteItems: () => void;
  canMoveToSilo: boolean;
}

const MoveToToggleMenu = (props: MoveToToggleMenuProps) => {
  const {
    setIsOpened,
    delivery,
    handleMoveItem,
    selectedCart,
    t,
    selectedLocations,
    handleDeleteItems,
    canMoveToSilo,
  } = props;
  const [toggleShipmentSelect, setToggleShipmentSelect] = useState(true);

  const ref = useRef(null);

  useClickOutsideHandler(ref, () => {
    setIsOpened(false);
    setToggleShipmentSelect(false);
  });

  const handleSeletNewShipment = (id: string | null) => {
    setToggleShipmentSelect(false);
    setIsOpened(false);
    handleMoveItem(id);
  };

  const isPickupEligible = (
    pickup: OrderDelivery,
    commonLocationIds: Set<string>,
  ) => {
    return (
      pickup.locationID &&
      !pickup.siloID &&
      (commonLocationIds.has(pickup.locationID) ||
        pickup.type === DeliveryType.Delivery)
    );
  };

  const eligibleDeliveries = useMemo(() => {
    const commonLocationIds = getCommonLocationIds(selectedLocations);
    return selectedCart.filter(
      (shipment) =>
        shipment.id !== delivery.id &&
        isPickupEligible(shipment, commonLocationIds),
    );
  }, [delivery.id, selectedCart, selectedLocations]);

  return (
    <div
      className="absolute p-1-4 rounded-8-0 shadow border border-primary-grey right-0 bg-white z-3 mt-1-0 w-[300px]"
      ref={ref}
    >
      <div className="flex flex-col text-body1 leading-1-5 gap-1-0">
        {eligibleDeliveries.length > 0 && (
          <>
            <p>{t('checkout.move_to')}</p>
            <Accordion isOpen={toggleShipmentSelect}>
              <div className="flex flex-col gap-y-1-0 text-body1 leading-1-5 pl-2-0 font-md pb-1-0">
                {eligibleDeliveries.map((shipment) => (
                  <p
                    className="cursor-pointer border-b border-primary-grey pb-1-0"
                    key={shipment.id}
                    onClick={() => handleSeletNewShipment(shipment.id)}
                  >
                    {shipment.type === DeliveryType.Pickup
                      ? `${t('checkout.pickup')} ${shipment.id}`
                      : `${t('checkout.shipment')} ${shipment.id}`}
                  </p>
                ))}
              </div>
            </Accordion>
          </>
        )}
        <p
          className="cursor-pointer last:pb-0"
          onClick={() => handleSeletNewShipment(null)}
        >
          {t('checkout.move_to_different')}
        </p>
        <p
          className="cursor-pointer"
          onClick={() => handleSeletNewShipment('0')}
        >
          {delivery.type === DeliveryType.Pickup
            ? t('checkout.move_to_different_pickup')
            : t('checkout.move_to_pickup')}
        </p>
        {canMoveToSilo && !delivery.siloID && (
          <p
            className="cursor-pointer"
            onClick={() => handleSeletNewShipment('SILO')}
          >
            {t('checkout.move_to_silo')}
          </p>
        )}
        <p className="cursor-pointer" onClick={handleDeleteItems}>
          {t('checkout.delete')}
        </p>
      </div>
    </div>
  );
};

export default MoveToToggleMenu;
